import { Router } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  DEFAULT_PROFILE_AVATAR_SRC,
  SearchImage,
} from '../search-result-processor/search-result-processor.service';
import { OsintSearchService } from './osint-search.service';

/** Cache face-detection outcomes by detectUrl / src so platform cards share results. */
const faceDetectCache = new Map<string, boolean>();

/** Keep under backend Joi max (40). */
const FACE_DETECT_BATCH_SIZE = 20;

export type ImageSearchCandidate =
  | string
  | null
  | undefined
  | Pick<SearchImage, 'src' | 'detectUrl' | 'faceDetected'>;

export function hasFaceSearchPermission(): boolean {
  try {
    const raw = localStorage.getItem('USER_PORTAL');
    if (!raw) {
      return false;
    }
    const portal = JSON.parse(raw);
    return Boolean(
      portal?.accessDetails?.face_search_permission ||
        portal?.subscriptionDetails?.face_search_permission,
    );
  } catch {
    return false;
  }
}

/** Authenticated OSINT image proxy: /api/osint/image/{hash}_{source} */
const OSINT_IMAGE_PROXY_URL_RE = /\/api\/osint\/image\//i;

export function isAuthGatedOsintImageUrl(url?: string | null): boolean {
  return Boolean(url && OSINT_IMAGE_PROXY_URL_RE.test(url));
}

/**
 * Same as OSINT / basic search result: relative `/api/osint/image/...` must hit
 * `environment.baseUrl` host (e.g. :3000), never the Angular app (:4200).
 */
export function toAbsoluteOsintImageUrl(url?: string | null): string {
  if (!url || typeof url !== 'string') return String(url || '');
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }
  const base = String(environment.baseUrl || '').replace(/\/+$/, '');
  if (!base) return trimmed;
  if (trimmed.startsWith('/api/osint/image/')) {
    const origin = base.replace(/\/api$/i, '');
    return `${origin}${trimmed}`;
  }
  if (trimmed.startsWith('/osint/image/')) {
    return `${base}${trimmed}`;
  }
  // Raw wasabi identifier: hash_source
  if (/^[a-f0-9]{32}_[a-z0-9_]+$/i.test(trimmed)) {
    return `${base}/osint/image/${encodeURIComponent(trimmed)}`;
  }
  return trimmed;
}

export function isSearchableImageSrc(src?: string | null): boolean {
  if (!src) {
    return false;
  }
  if (src === DEFAULT_PROFILE_AVATAR_SRC || src.startsWith('data:image/svg')) {
    return false;
  }
  // Auth-gated proxy paths are searchable (link-analysis face_image nodes, etc.).
  // Face-detect cache / faceDetected still gate canStartImageSearch for non-confirmed faces.
  if (isAuthGatedOsintImageUrl(src)) {
    return true;
  }
  return (
    src.startsWith('http://') ||
    src.startsWith('https://') ||
    src.startsWith('data:image/') ||
    src.startsWith('blob:')
  );
}

export function getFaceDetectCache(key?: string | null): boolean | undefined {
  if (!key) {
    return undefined;
  }
  return faceDetectCache.get(key);
}

export function setFaceDetectCache(key: string, faceDetected: boolean): void {
  if (!key) {
    return;
  }
  faceDetectCache.set(key, faceDetected);
}

/** True when backend finished async face detect (history / completed new search). */
export function isFaceDetectionReady(payload: any): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  if (payload.face_detection_ready === true) {
    return true;
  }
  if (payload.data?.face_detection_ready === true) {
    return true;
  }
  if (payload.data_list?.[0]?.face_detection_ready === true) {
    return true;
  }
  const map =
    (payload.faceDetected && typeof payload.faceDetected === 'object'
      ? payload.faceDetected
      : null) ||
    (payload.data?.faceDetected && typeof payload.data.faceDetected === 'object'
      ? payload.data.faceDetected
      : null) ||
    (payload.data_list?.[0]?.faceDetected &&
    typeof payload.data_list[0].faceDetected === 'object'
      ? payload.data_list[0].faceDetected
      : null) ||
    (payload.extra_data?.faceDetected &&
    typeof payload.extra_data.faceDetected === 'object'
      ? payload.extra_data.faceDetected
      : null);
  return Boolean(map && Object.keys(map).length > 0);
}

/** True while backend is still computing faceDetected flags. */
export function isFaceDetectionPending(payload: any): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  return Boolean(
    payload.face_detection_pending === true ||
      payload.data?.face_detection_pending === true ||
      payload.data_list?.[0]?.face_detection_pending === true ||
      payload.extra_data?.face_detection_pending === true,
  );
}

/**
 * New live search: wait for server flags instead of a manual reload.
 * History: already ready → do not soft-poll.
 * Legacy rows (no status flags): do not soft-poll — fall back to one-shot client detect.
 */
export function shouldSoftPollFaceDetection(payload: any): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  if (isFaceDetectionReady(payload)) {
    return false;
  }
  if (isFaceDetectionPending(payload)) {
    return true;
  }

  // Explicit "not ready yet" from backend (new search after markPending).
  const explicitNotReady =
    payload.face_detection_ready === false ||
    payload.data?.face_detection_ready === false ||
    payload.data_list?.[0]?.face_detection_ready === false ||
    payload.extra_data?.face_detection_ready === false;
  if (!explicitNotReady) {
    return false;
  }

  const done = Boolean(
    payload.done === true ||
      payload.data?.done === true ||
      payload.vendor_sync_complete === true,
  );
  const refreshPending = Boolean(
    payload.background_refresh_pending === true ||
      payload.data?.background_refresh_pending === true,
  );
  return done && !refreshPending;
}

function resolveDetectUrl(image: ImageSearchCandidate): string | null {
  if (!image) {
    return null;
  }
  if (typeof image === 'string') {
    return image;
  }
  return image.detectUrl || image.src || null;
}

/**
 * Camera / image-search trigger is shown only when:
 * - user has face search permission
 * - image src is searchable
 * - local face detection confirmed a human face
 */
export function canStartImageSearch(image?: ImageSearchCandidate): boolean {
  if (!hasFaceSearchPermission()) {
    return false;
  }

  const src = typeof image === 'string' ? image : image?.src;
  if (!isSearchableImageSrc(src)) {
    return false;
  }

  if (typeof image === 'object' && image?.faceDetected === true) {
    return true;
  }

  const detectUrl = resolveDetectUrl(image);
  if (detectUrl && faceDetectCache.get(detectUrl) === true) {
    return true;
  }
  if (src && faceDetectCache.get(src) === true) {
    return true;
  }

  return false;
}

function isApiDetectableUrl(url: string): boolean {
  if (!url || url.startsWith('blob:')) {
    return false;
  }
  if (url.startsWith('data:image/svg')) {
    return false;
  }
  return (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:image/')
  );
}

function getAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('USER_PORTAL');
    if (!raw) {
      return null;
    }
    const portal = JSON.parse(raw);
    return portal?.loginDetails?.access_token || null;
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function blobUrlToDataUrl(blobUrl: string): Promise<string> {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return blobToDataUrl(blob);
}

async function authorizedUrlToDataUrl(url: string): Promise<string> {
  const absolute = toAbsoluteOsintImageUrl(url);
  const token = getAccessToken();
  const response = await fetch(absolute, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Unable to load image (${response.status})`);
  }
  return blobToDataUrl(await response.blob());
}

const displayableOsintImageCache = new Map<string, Promise<string | null>>();

/**
 * Convert auth-gated `/api/osint/image/...` URLs into data URLs the browser can
 * render in <img> tags. Public http(s) URLs are returned unchanged.
 */
export function resolveDisplayableOsintImageUrl(
  url?: string | null,
): Promise<string | null> {
  const trimmed = toAbsoluteOsintImageUrl(url);
  if (!trimmed) return Promise.resolve(null);
  if (!isAuthGatedOsintImageUrl(trimmed)) return Promise.resolve(trimmed);

  const cached = displayableOsintImageCache.get(trimmed);
  if (cached) return cached;

  const pending = authorizedUrlToDataUrl(trimmed).catch(() => null);
  displayableOsintImageCache.set(trimmed, pending);
  return pending;
}

/**
 * Prefer in-browser image bytes (blob / data URL) over auth-gated proxy URLs.
 * Sending `/api/osint/image/...` as a public URL makes face search 401.
 */
export function resolveImageSearchSrc(
  imageOrSrc: ImageSearchCandidate | string,
): string | null {
  if (!imageOrSrc) {
    return null;
  }
  if (typeof imageOrSrc === 'string') {
    return imageOrSrc;
  }

  const src = imageOrSrc.src || null;
  if (src?.startsWith('blob:') || src?.startsWith('data:image/')) {
    return src;
  }
  if (isAuthGatedOsintImageUrl(imageOrSrc.detectUrl) && src) {
    return src;
  }

  return imageOrSrc.detectUrl || src;
}

/**
 * Prefer data URLs already in the browser (blob display) so the backend
 * does not need to re-download CDN/auth-gated images.
 */
async function resolveDetectPayload(
  image: SearchImage,
): Promise<{ cacheKeys: string[]; payload: string } | null> {
  const detectUrl = image.detectUrl || image.src;
  const cacheKeys = [detectUrl, image.src].filter(
    (value, index, arr): value is string =>
      !!value && arr.indexOf(value) === index,
  );

  // Proxy URLs are loaded from Wasabi on the server. Do not convert the
  // already-displayed blob into a huge data URL just to re-upload it.
  if (detectUrl && isAuthGatedOsintImageUrl(detectUrl)) {
    return { cacheKeys, payload: detectUrl };
  }

  if (image.src?.startsWith('blob:')) {
    try {
      const dataUrl = await blobUrlToDataUrl(image.src);
      if (
        dataUrl.startsWith('data:image/') &&
        !dataUrl.startsWith('data:image/svg')
      ) {
        return { cacheKeys, payload: dataUrl };
      }
    } catch {
      // fall through to detectUrl
    }
  }

  if (detectUrl && isApiDetectableUrl(detectUrl)) {
    return { cacheKeys, payload: detectUrl };
  }

  return null;
}

function applyFaceResultsToImages(images: SearchImage[]): SearchImage[] {
  return images.map((image) => {
    const keys = [image.detectUrl, image.src].filter(Boolean) as string[];
    const faceDetected = keys.some((key) => faceDetectCache.get(key) === true);
    return { ...image, faceDetected };
  });
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

function firstValueFromCompat<T>(source: Observable<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const subscription = source.subscribe({
      next: (value) => {
        resolve(value);
        subscription.unsubscribe();
      },
      error: (error) => {
        reject(error);
      },
      complete: () => {
        // no-op; next already resolved for HTTP observables
      },
    });
  });
}

/**
 * Resolve a persisted faceDetected boolean for a gallery image from the DB map.
 */
export function lookupPersistedFaceDetected(
  image: SearchImage | ImageSearchCandidate,
  faceDetectedMap?: Record<string, boolean> | null,
): boolean | undefined {
  if (!faceDetectedMap || typeof faceDetectedMap !== 'object') {
    return undefined;
  }

  const candidates: string[] = [];
  if (typeof image === 'string') {
    candidates.push(image);
  } else if (image && typeof image === 'object') {
    if (image.detectUrl) candidates.push(String(image.detectUrl));
    if (image.src) candidates.push(String(image.src));
    if ((image as SearchImage).faceDetected === true) return true;
    if ((image as SearchImage).faceDetected === false) return false;
  }

  for (const raw of candidates) {
    if (!raw) continue;
    if (faceDetectedMap[raw] !== undefined) {
      return Boolean(faceDetectedMap[raw]);
    }
    const noQuery = raw.split('?')[0];
    if (noQuery && faceDetectedMap[noQuery] !== undefined) {
      return Boolean(faceDetectedMap[noQuery]);
    }
    const proxyMatch = raw.match(
      /\/api\/osint\/image\/([a-f0-9]{32}_[a-z0-9_]+)/i,
    );
    if (proxyMatch?.[1] && faceDetectedMap[proxyMatch[1]] !== undefined) {
      return Boolean(faceDetectedMap[proxyMatch[1]]);
    }
  }
  return undefined;
}

/**
 * Apply DB faceDetected flags to images.
 * When a persisted map exists, this is the only source of truth — no API call.
 * When face detection is still pending on the server, skip the client API (soft-poll will retry).
 * When no map exists (legacy), runs face detect once.
 */
export function annotateImagesWithFaceDetection(
  osintSearchService: OsintSearchService,
  images: SearchImage[],
  faceDetectedMap?: Record<string, boolean> | null,
  options?: { faceDetectionPending?: boolean },
): Observable<SearchImage[]> {
  if (!images?.length) {
    return of([]);
  }

  if (!hasFaceSearchPermission()) {
    return of(
      images.map((image) => ({
        ...image,
        faceDetected: false,
      })),
    );
  }

  const hasPersistedMap =
    faceDetectedMap &&
    typeof faceDetectedMap === 'object' &&
    Object.keys(faceDetectedMap).length > 0;

  // History / completed search: use DB flags only — never re-detect.
  if (hasPersistedMap) {
    for (const [key, value] of Object.entries(faceDetectedMap!)) {
      if (key) {
        faceDetectCache.set(key, Boolean(value));
      }
    }
    return of(
      images.map((image) => {
        const fromMap = lookupPersistedFaceDetected(image, faceDetectedMap);
        const faceDetected =
          fromMap !== undefined
            ? fromMap
            : image.faceDetected === true ||
              faceDetectCache.get(String(image.detectUrl || '')) === true ||
              faceDetectCache.get(String(image.src || '')) === true;
        if (image.detectUrl) {
          faceDetectCache.set(image.detectUrl, Boolean(faceDetected));
        }
        if (image.src) {
          faceDetectCache.set(image.src, Boolean(faceDetected));
        }
        return { ...image, faceDetected: Boolean(faceDetected) };
      }),
    );
  }

  // Server still detecting — wait for soft-poll; do not thrash /osint/face-detect.
  if (options?.faceDetectionPending) {
    return of(
      images.map((image) => ({
        ...image,
        faceDetected: image.faceDetected === true,
      })),
    );
  }

  // New search (no DB flags yet): detect once so the search icon can appear.
  return from(
    (async () => {
      const payloadItems: Array<{ cacheKeys: string[]; payload: string }> = [];
      const seenPayload = new Set<string>();

      for (const image of images) {
        if (image.faceDetected === true || image.faceDetected === false) {
          const keys = [image.detectUrl, image.src].filter(Boolean) as string[];
          keys.forEach((key) => faceDetectCache.set(key, image.faceDetected === true));
          continue;
        }

        const resolved = await resolveDetectPayload(image);
        if (!resolved) {
          continue;
        }

        const alreadyCached = resolved.cacheKeys.some(
          (key) => faceDetectCache.get(key) !== undefined,
        );
        if (alreadyCached) {
          const known = resolved.cacheKeys
            .map((key) => faceDetectCache.get(key))
            .find((value) => value !== undefined);
          if (typeof known === 'boolean') {
            resolved.cacheKeys.forEach((key) =>
              faceDetectCache.set(key, known),
            );
          }
          continue;
        }

        if (seenPayload.has(resolved.payload)) {
          continue;
        }
        seenPayload.add(resolved.payload);
        payloadItems.push(resolved);
      }

      return payloadItems;
    })(),
  ).pipe(
    switchMap((payloadItems) => {
      if (payloadItems.length === 0) {
        return of(applyFaceResultsToImages(images));
      }

      const batches = chunkArray(payloadItems, FACE_DETECT_BATCH_SIZE);

      return from(
        (async () => {
          for (const batch of batches) {
            try {
              const response = await firstValueFromCompat(
                osintSearchService.detectHumanFaces({
                  urls: batch.map((item) => item.payload),
                }),
              );

              const results =
                response?.data?.results ?? response?.results ?? [];

              batch.forEach((item, index) => {
                const result = Array.isArray(results) ? results[index] : null;
                const faceDetected = Boolean(result?.faceDetected);
                item.cacheKeys.forEach((key) =>
                  faceDetectCache.set(key, faceDetected),
                );
                if (result?.url) {
                  faceDetectCache.set(result.url, faceDetected);
                }
              });
            } catch (error) {
              console.error('Face detection batch failed:', error);
              batch.forEach((item) => {
                item.cacheKeys.forEach((key) =>
                  faceDetectCache.set(key, false),
                );
              });
            }
          }

          return applyFaceResultsToImages(images);
        })(),
      );
    }),
    catchError((error) => {
      console.error('Face detection annotation failed:', error);
      return of(
        images.map((image) => ({
          ...image,
          faceDetected: false,
        })),
      );
    }),
  );
}

export async function navigateToOsintImageSearch(
  router: Router,
  src: string,
): Promise<void> {
  let imagePreview = src;

  if (src.startsWith('blob:')) {
    imagePreview = await blobUrlToDataUrl(src);
  } else if (isAuthGatedOsintImageUrl(src)) {
    imagePreview = await authorizedUrlToDataUrl(src);
  } else if (src.startsWith('http://') || src.startsWith('https://')) {
    await router.navigate(['/osint-image-search'], {
      state: { imageUrl: src },
    });
    return;
  }

  await router.navigate(['/osint-image-search'], {
    state: {
      imagePreview,
      uploadedFileName: 'osint-result-image',
    },
  });
}
